/**
 * Bottom multi-track timeline for the editor V2 rewrite (task 7).
 *
 * Ported from the `VideoEditor.tsx` monolith's timeline region (ruler +
 * draggable playhead + per-track clip/layer blocks + trim handles + clip
 * context menu), re-skinned in the dark V2 theme and wired to the Zustand
 * store instead of the monolith's local `useState` setters.
 *
 * Layout model (differs from the monolith): instead of a single percentage-
 * scaled slider, the timeline is a fixed left track-label column plus a
 * horizontally-scrollable lane area whose content width is
 * `duration × pixelsPerSecond`. `pixelsPerSecond` is LOCAL component state
 * driven by the zoom control, so every block/tick/playhead maps time↔pixels
 * through the same factor: `x = time × pps`, `time = x / pps`.
 *
 * Undo/redo: wired to the store's coalesced-snapshot history
 * (`useEditorStore().undo/redo`, `selectCanUndo/selectCanRedo`). The global
 * Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z / Ctrl+Y hotkeys live in `EditorShell`
 * (always mounted), not here.
 */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';
import {
  Blend,
  Copy,
  Film,
  Layers,
  Eye,
  EyeOff,
  Lock,
  Magnet,
  MousePointer2,
  Redo2,
  Scissors,
  Trash2,
  Unlock,
  Undo2,
  ZoomIn,
  ZoomOut,
  Volume2,
  VolumeX,
} from 'lucide-react';
import {
  selectActiveClip,
  selectCanRedo,
  selectCanUndo,
  selectDuration,
  useEditorStore,
} from '@/lib/editor/editorStore';
import type {
  EditorOverlayLayer,
  EditorTimelineClip,
  EditorTrackControlState,
  EditorTrackKind,
} from '@/types/editor';
import { AudioTimelineRow } from './AudioTimelineRow';
import { TimelineTrimHandles } from './TimelineTrimHandles';
import { cn } from '@/lib/utils/cn';
import {
  effectiveTransitionDuration,
  renderStartTimes,
  TRANSITION_LABELS,
} from '@/lib/editor/transitions';
import { packTimelineLanes } from '@/lib/editor/timelineLanes';
import {
  collectTimelineSnapPoints,
  snapTimelineRange,
  snapTimelineTime,
} from '@/lib/editor/timelineSnapping';
import { formatFrameTimecode, snapTimeToFrame } from '@/lib/editor/frameGrid';
import { cleanLayerKeyframes } from '@/lib/editor/layerKeyframes';

// ---------------------------------------------------------------------------
// Constants / helpers
// ---------------------------------------------------------------------------

const LABEL_WIDTH = 132; // px, left track-label column including track controls
const RULER_HEIGHT = 22; // px, top ruler strip
const MIN_PPS = 12;
const MAX_PPS = 160;
const DEFAULT_PPS = 48;

function safeClipDuration(value?: number) {
  return Number.isFinite(value) && value && value > 0 ? value : 6;
}

function clampTime(value: number, duration: number) {
  return Math.min(Math.max(value, 0), Math.max(duration, 0));
}

/** `mm:ss` — matches the monolith `formatDuration`. */
function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** `mm:ss.d` — matches the monolith / EditorPreview `formatTimecode`. */
function formatTimecode(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00.0';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const tenth = Math.floor((seconds % 1) * 10);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${tenth}`;
}

interface ContextMenuState {
  x: number;
  y: number;
  clipId: string;
}

// ---------------------------------------------------------------------------
// EditorTimeline
// ---------------------------------------------------------------------------

export function EditorTimeline() {
  // ---- store reads
  const clips = useEditorStore((s) => s.clips);
  const layers = useEditorStore((s) => s.layers);
  const audioTracks = useEditorStore((s) => s.audioTracks);
  const selectedAudioTrackId = useEditorStore((s) => s.selectedAudioTrackId);
  const currentTime = useEditorStore((s) => s.currentTime);
  const selectedClipId = useEditorStore((s) => s.selectedClipId);
  const duration = useEditorStore((s) => selectDuration(s));
  const activeClip = useEditorStore((s) => selectActiveClip(s, s.currentTime));
  const canUndo = useEditorStore(selectCanUndo);
  const canRedo = useEditorStore(selectCanRedo);
  const trackSettings = useEditorStore((s) => s.trackSettings);
  const timelineInsertMode = useEditorStore((s) => s.timelineInsertMode);

  // ---- store actions
  const selectClip = useEditorStore((s) => s.selectClip);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const selectAudioTrack = useEditorStore((s) => s.selectAudioTrack);
  const updateAudioTrack = useEditorStore((s) => s.updateAudioTrack);
  const setTimelineInsertMode = useEditorStore((s) => s.setTimelineInsertMode);
  const removeClip = useEditorStore((s) => s.removeClip);
  const duplicateClip = useEditorStore((s) => s.duplicateClip);
  const splitClipAtPlayhead = useEditorStore((s) => s.splitClipAtPlayhead);
  const trimClipIn = useEditorStore((s) => s.trimClipIn);
  const trimClipOut = useEditorStore((s) => s.trimClipOut);
  const reorderClips = useEditorStore((s) => s.reorderClips);
  const setCurrentTime = useEditorStore((s) => s.setCurrentTime);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const setTrackSettings = useEditorStore((s) => s.setTrackSettings);

  // ---- local UI state
  const [pixelsPerSecond, setPixelsPerSecond] = useState(DEFAULT_PPS);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [snappingEnabled, setSnappingEnabled] = useState(true);
  const [snapGuideTime, setSnapGuideTime] = useState<number | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const userZoomedRef = useRef(false);
  // Set true while a pointer went down on a trim handle so the native
  // clip drag-to-reorder doesn't hijack the trim gesture.
  const suppressDragRef = useRef(false);

  const safeDuration = Math.max(duration, 1);
  const displayClips = useMemo(() => {
    const starts = renderStartTimes(clips);
    return clips.map((clip, index) => ({ ...clip, start: starts[index] ?? clip.start }));
  }, [clips]);

  // Fit-to-width: the min zoom makes the whole edit exactly fill the visible
  // lane, so it can never be squished to an unreadable sliver; the default
  // zoom is this fit (length picked from the current clips), until the user
  // zooms manually.
  // Leave ~20% runway to the right of the content so the fit isn't edge-to-edge.
  const FIT_HEADROOM = 1.22;
  const fitPps =
    viewportWidth > 0 ? Math.min(400, Math.max(6, viewportWidth / (safeDuration * FIT_HEADROOM))) : DEFAULT_PPS;
  const minPps = fitPps;
  const maxPps = Math.max(MAX_PPS, Math.round(fitPps * 6));
  const pps = Math.min(maxPps, Math.max(minPps, pixelsPerSecond));
  const contentWidth = Math.max(Math.round(safeDuration * pps), 1);

  // Measure the visible lane width (drives fit-to-width).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Default zoom = fit-to-width until the user manually zooms.
  useEffect(() => {
    if (userZoomedRef.current) return;
    if (viewportWidth > 0) setPixelsPerSecond(fitPps);
  }, [viewportWidth, fitPps]);

  const overlayLanes = useMemo(() => {
    const packed = packTimelineLanes(layers);
    return packed.length > 0 ? packed : [[] as EditorOverlayLayer[]];
  }, [layers]);
  const snapPoints = useMemo(
    () => collectTimelineSnapPoints(displayClips, layers, duration),
    [displayClips, layers, duration]
  );

  // -----------------------------------------------------------------------
  // Playhead / scrub (x ↔ time through `pps`)
  // -----------------------------------------------------------------------

  const seekToClientX = useCallback(
    (clientX: number) => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;
      const rawTime = clampTime(x / Math.max(pps, 1), duration);
      const snapped = snappingEnabled
        ? snapTimelineTime(rawTime, snapPoints, 8 / Math.max(pps, 1))
        : { value: rawTime, snappedTo: null };
      setSnapGuideTime(snapped.snappedTo);
      setCurrentTime(clampTime(snapTimeToFrame(snapped.value), duration));
    },
    [pps, duration, setCurrentTime, snappingEnabled, snapPoints]
  );

  const scrub = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest('[data-timeline-control]')) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      seekToClientX(event.clientX);
    },
    [seekToClientX]
  );

  const scrubMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.buttons !== 1) return;
      const target = event.target as HTMLElement;
      if (target.closest('[data-timeline-control]')) return;
      seekToClientX(event.clientX);
    },
    [seekToClientX]
  );

  const endScrub = useCallback(() => setSnapGuideTime(null), []);

  // -----------------------------------------------------------------------
  // Clip block interactions
  // -----------------------------------------------------------------------

  const handleClipContextMenu = useCallback(
    (clip: EditorTimelineClip, event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / Math.max(rect.width, 1)));
      const time = clampTime(clip.start + ratio * safeClipDuration(clip.duration), duration);
      // Select + park the playhead inside the clip so a subsequent
      // "split at playhead" cuts this clip at the clicked position.
      selectClip(clip.id);
      setCurrentTime(time);
      setContextMenu({
        x: Math.min(event.clientX, window.innerWidth - 220),
        y: Math.min(event.clientY, window.innerHeight - 180),
        clipId: clip.id,
      });
    },
    [duration, selectClip, setCurrentTime]
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // Close the context menu on any outside interaction / Escape.
  useEffect(() => {
    if (!contextMenu) return;
    const onPointerDown = () => setContextMenu(null);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [contextMenu]);

  // ---- drag-to-reorder (native DnD; suppressed while trimming)
  const handleDragStart = useCallback(
    (index: number) => (event: React.DragEvent<HTMLElement>) => {
      if (suppressDragRef.current) {
        event.preventDefault();
        return;
      }
      setDragIndex(index);
      event.dataTransfer.effectAllowed = 'move';
      // Firefox requires data to be set for drag to start.
      event.dataTransfer.setData('text/plain', String(index));
    },
    []
  );

  const handleDragOver = useCallback(
    (index: number) => (event: React.DragEvent<HTMLElement>) => {
      if (dragIndex === null) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (index !== overIndex) setOverIndex(index);
    },
    [dragIndex, overIndex]
  );

  const handleDrop = useCallback(
    (index: number) => (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      if (dragIndex !== null && dragIndex !== index) {
        reorderClips(dragIndex, index);
      }
      setDragIndex(null);
      setOverIndex(null);
    },
    [dragIndex, reorderClips]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  // -----------------------------------------------------------------------
  // Toolbar actions
  // -----------------------------------------------------------------------

  const canSplit = Boolean(activeClip);
  const canMutateSelected = Boolean(selectedClipId);

  const handleSplit = useCallback(() => splitClipAtPlayhead(), [splitClipAtPlayhead]);
  const handleDelete = useCallback(() => {
    if (selectedClipId) removeClip(selectedClipId);
  }, [selectedClipId, removeClip]);
  const handleDuplicate = useCallback(() => {
    if (selectedClipId) duplicateClip(selectedClipId);
  }, [selectedClipId, duplicateClip]);

  const zoomBy = useCallback(
    (factor: number) => {
      userZoomedRef.current = true;
      setPixelsPerSecond((prev) => Math.min(maxPps, Math.max(minPps, Math.round(prev * factor))));
    },
    [minPps, maxPps]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const playheadLeft = Math.min(currentTime, safeDuration) * pps;

  return (
    <section className="flex min-h-0 flex-col border-t border-white/10 bg-[#111113]">
      {/* Toolbar */}
      <div className="flex h-11 shrink-0 items-center gap-2 border-b border-white/10 px-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-white/40">
          <MousePointer2 className="h-3.5 w-3.5" />
          Таймлайн
        </div>

        <div className="mx-1 h-5 w-px bg-white/10" />

        {/* Undo / Redo */}
        <ToolbarButton icon={Undo2} label="Отменить" disabled={!canUndo} onClick={undo} />
        <ToolbarButton icon={Redo2} label="Повторить" disabled={!canRedo} onClick={redo} />
        <button
          type="button"
          onClick={() => setSnappingEnabled((enabled) => !enabled)}
          aria-pressed={snappingEnabled}
          aria-label={snappingEnabled ? 'Выключить привязку' : 'Включить привязку'}
          title={snappingEnabled ? 'Привязка включена' : 'Привязка выключена'}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg transition',
            snappingEnabled
              ? 'bg-brand-500/20 text-brand-300'
              : 'text-white/45 hover:bg-white/10 hover:text-white'
          )}
        >
          <Magnet className="h-4 w-4" />
        </button>

        <div className="mx-1 h-5 w-px bg-white/10" />

        <ToolbarButton icon={Scissors} label="Разрезать" disabled={!canSplit} onClick={handleSplit} />
        <ToolbarButton icon={Copy} label="Дублировать" disabled={!canMutateSelected} onClick={handleDuplicate} />
        <ToolbarButton
          icon={Trash2}
          label="Удалить"
          disabled={!canMutateSelected}
          onClick={handleDelete}
          danger
        />

        <div className="mx-1 h-5 w-px bg-white/10" />
        <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-0.5" aria-label="Режим добавления клипов">
          {([
            ['append', 'В конец'],
            ['insert', 'Вставка'],
            ['overwrite', 'Замена'],
          ] as const).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => setTimelineInsertMode(mode)}
              aria-pressed={timelineInsertMode === mode}
              title={mode === 'insert' ? 'Ripple-вставка в позицию плейхеда' : mode === 'overwrite' ? 'Заменить интервал от плейхеда' : 'Добавить после последнего клипа'}
              className={cn(
                'h-6 rounded-md px-2 text-[9px] font-bold transition',
                timelineInsertMode === mode
                  ? 'bg-brand-500/25 text-brand-200'
                  : 'text-white/35 hover:text-white/70'
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <div className="mr-1 text-xs font-semibold tabular-nums text-white/50">
          <span title="Часы:минуты:секунды:кадры">{formatFrameTimecode(currentTime)}</span>
          <span className="text-white/25"> / {formatTimecode(duration)}</span>
        </div>

        {/* Zoom (pixels-per-second, local state) */}
        <div className="flex items-center gap-1 rounded-full bg-white/5 px-1 py-0.5">
          <button
            type="button"
            aria-label="Уменьшить масштаб"
            onClick={() => zoomBy(1 / 1.25)}
            disabled={pps <= minPps + 0.5}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-30"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <input
            type="range"
            min={minPps}
            max={maxPps}
            step={1}
            value={pps}
            onChange={(e) => {
              userZoomedRef.current = true;
              setPixelsPerSecond(Number(e.target.value));
            }}
            aria-label="Масштаб таймлайна"
            className="h-1 w-20 cursor-pointer accent-brand-500"
          />
          <button
            type="button"
            aria-label="Увеличить масштаб"
            onClick={() => zoomBy(1.25)}
            disabled={pps >= maxPps - 0.5}
            className="flex h-6 w-6 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white disabled:opacity-30"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body: left labels + scrollable lanes (fills remaining height; scrolls vertically if tracks overflow) */}
      <div className="flex min-h-0 flex-1 overflow-y-auto">
        {/* Left track-label column */}
        <div
          className="flex min-h-full shrink-0 flex-col border-r border-white/10 bg-white/[0.02]"
          style={{ width: LABEL_WIDTH }}
        >
          <div style={{ height: RULER_HEIGHT }} className="shrink-0 border-b border-white/5" />
          <div className="flex flex-col gap-1 p-1.5">
            <TrackLabel
              icon={<Film className="h-3.5 w-3.5 text-white/40" />}
              label="Видео"
              rowClassName="h-[56px] shrink-0"
              kind="video"
              settings={trackSettings.video}
              onChange={setTrackSettings}
              allowMute
            />
            {(audioTracks.length ? audioTracks : [null]).map((track, index) => (
              <TrackLabel
                key={track?.id ?? 'empty-music-lane'}
                icon={<span className="text-sm leading-none">🎵</span>}
                label={track?.fileName ? `Аудио ${index + 1}` : 'Музыка'}
                rowClassName="h-[32px] shrink-0"
                kind="music"
                settings={trackSettings.music}
                onChange={setTrackSettings}
                allowMute
              />
            ))}
            {overlayLanes.map((lane, index) => (
              <TrackLabel
                key={lane[0]?.id ?? 'empty-overlay-lane'}
                icon={<Layers className="h-3.5 w-3.5 text-white/40" />}
                label={`Слой ${index + 1}`}
                rowClassName="h-[32px] shrink-0"
                kind="overlays"
                settings={trackSettings.overlays}
                onChange={setTrackSettings}
              />
            ))}
          </div>
        </div>

        {/* Scrollable lane area */}
        <div ref={viewportRef} data-lenis-prevent className="min-w-0 flex-1 overflow-x-auto">
          <div
            ref={contentRef}
            role="slider"
            tabIndex={0}
            aria-label="Таймлайн"
            aria-valuemin={0}
            aria-valuemax={Math.max(duration, 0)}
            aria-valuenow={currentTime}
            onPointerDown={scrub}
            onPointerMove={scrubMove}
            onPointerUp={endScrub}
            onPointerCancel={endScrub}
            className="relative flex min-h-full cursor-ew-resize flex-col"
            style={{ width: contentWidth, minWidth: '100%' }}
          >
            {/* Ruler */}
            <Ruler duration={duration} pps={pps} height={RULER_HEIGHT} />

            {/* Playhead */}
            <span
              className="pointer-events-none absolute z-40 w-0.5 bg-white shadow-[0_0_0_1px_rgba(91,53,255,0.9)]"
              style={{ left: playheadLeft, top: RULER_HEIGHT - 4, bottom: 4 }}
            >
              <span className="absolute -left-[7px] -top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-brand-600 shadow-lg" />
            </span>
            {snapGuideTime !== null && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute z-30 w-px bg-cyan-300/90 shadow-[0_0_8px_rgba(103,232,249,.7)]"
                style={{ left: snapGuideTime * pps, top: RULER_HEIGHT, bottom: 4 }}
              />
            )}

            {/* Ruler spacer — mirrors the label column's spacer so row N's lane lines up with row N's label */}
            <div style={{ height: RULER_HEIGHT }} className="shrink-0" />

            {/* Lanes */}
            <div className="flex flex-col gap-1 p-1.5">
              {/* Видео */}
              <div className={cn(
                'relative h-[56px] shrink-0 overflow-hidden rounded-lg bg-white/[0.03] transition',
                trackSettings.video.hidden && 'opacity-30'
              )}>
                {clips.length === 0 ? (
                  <div className="absolute inset-y-1.5 left-2 flex items-center rounded-md bg-white/5 px-2.5 text-[11px] font-semibold text-white/40">
                    Добавьте видео на таймлайн
                  </div>
                ) : (
                  <>
                    {displayClips.map((clip, index) => (
                      <VideoClipBlock
                        key={clip.id}
                        clip={clip}
                        index={index}
                        pps={pps}
                        selected={clip.id === selectedClipId}
                        isDragging={dragIndex === index}
                        isDropTarget={overIndex === index && dragIndex !== null && dragIndex !== index}
                        suppressDragRef={suppressDragRef}
                        onSelect={selectClip}
                        onContextMenu={handleClipContextMenu}
                        onTrimIn={trimClipIn}
                        onTrimOut={trimClipOut}
                        onDragStart={handleDragStart(index)}
                        onDragOver={handleDragOver(index)}
                        onDrop={handleDrop(index)}
                        onDragEnd={handleDragEnd}
                        locked={trackSettings.video.locked}
                      />
                    ))}
                    {displayClips.slice(0, -1).map((clip, index) => {
                      const nextClip = clips[index + 1];
                      const transition = nextClip?.transitionIn;
                      if (!nextClip || !transition) return null;
                      const durationSec = effectiveTransitionDuration(
                        clip.duration,
                        nextClip.duration,
                        transition
                      );
                      return (
                        <TransitionMarker
                          key={`transition-${nextClip.id}`}
                          windowStart={displayClips[index + 1].start}
                          durationSec={durationSec}
                          label={TRANSITION_LABELS[transition.type]}
                          pps={pps}
                          onSelect={() => {
                            selectClip(clip.id);
                            setActiveTool('transitions');
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </div>

              {/* Несколько независимых аудиодорожек */}
              {audioTracks.map((track) => (
                <div
                  key={track.id}
                  className={cn('relative h-[32px] shrink-0 transition', trackSettings.music.hidden && 'opacity-30')}
                >
                  <AudioTimelineRow
                    audioTrack={track}
                    maxDurationSeconds={safeDuration}
                    selected={selectedAudioTrackId === track.id}
                    onSelect={selectAudioTrack}
                    onChange={updateAudioTrack}
                  />
                </div>
              ))}
              {audioTracks.length === 0 && (
                <div className={cn('relative h-[32px] shrink-0 transition', trackSettings.music.hidden && 'opacity-30')}>
                  <div className="flex h-full items-center rounded-lg bg-white/[0.02] px-2.5 text-[10px] font-semibold text-white/25">
                    Нет аудио
                  </div>
                </div>
              )}

              {overlayLanes.map((lane) => (
                <LayerLane
                  key={lane[0]?.id ?? 'empty-overlay-lane'}
                  blocks={lane}
                  pps={pps}
                  emptyLabel="Нет слоёв"
                  onSelect={selectLayer}
                  onSeek={setCurrentTime}
                  duration={duration}
                  snapPoints={snapPoints}
                  snappingEnabled={snappingEnabled}
                  locked={trackSettings.overlays.locked}
                  hidden={trackSettings.overlays.hidden}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Clip context menu */}
      {contextMenu && (
        <div
          data-timeline-control
          className="fixed z-[80] w-[200px] overflow-hidden rounded-xl border border-white/10 bg-[#150927] p-1 text-xs font-semibold shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              handleSplit();
              closeContextMenu();
            }}
            className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-white/80 transition hover:bg-brand-500/15 hover:text-brand-200"
          >
            <Scissors className="h-3.5 w-3.5" />
            Разрезать по курсору
          </button>
          <button
            type="button"
            onClick={() => {
              duplicateClip(contextMenu.clipId);
              closeContextMenu();
            }}
            className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-white/80 transition hover:bg-white/10"
          >
            <Copy className="h-3.5 w-3.5" />
            Дублировать
          </button>
          <button
            type="button"
            onClick={() => {
              removeClip(contextMenu.clipId);
              closeContextMenu();
            }}
            className="flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-red-400 transition hover:bg-red-500/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Удалить
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-30',
        danger && 'hover:bg-red-500/15 hover:text-red-400'
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Left track label
// ---------------------------------------------------------------------------

function TrackLabel({
  icon,
  label,
  rowClassName,
  kind,
  settings,
  onChange,
  allowMute = false,
}: {
  icon: React.ReactNode;
  label: string;
  rowClassName: string;
  kind: EditorTrackKind;
  settings: EditorTrackControlState;
  onChange: (kind: EditorTrackKind, patch: Partial<EditorTrackControlState>) => void;
  allowMute?: boolean;
}) {
  return (
    <div className={cn('group flex items-center gap-1 text-[11px] font-semibold text-white/60', rowClassName)}>
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <span className="flex shrink-0 items-center">
        {allowMute && (
          <TrackControlButton
            label={settings.muted ? 'Включить звук дорожки' : 'Выключить звук дорожки'}
            active={settings.muted}
            onClick={() => onChange(kind, { muted: !settings.muted })}
            icon={settings.muted ? VolumeX : Volume2}
          />
        )}
        <TrackControlButton
          label={settings.hidden ? 'Показать дорожку' : 'Скрыть дорожку'}
          active={settings.hidden}
          onClick={() => onChange(kind, { hidden: !settings.hidden })}
          icon={settings.hidden ? EyeOff : Eye}
        />
        <TrackControlButton
          label={settings.locked ? 'Разблокировать дорожку' : 'Заблокировать дорожку'}
          active={settings.locked}
          onClick={() => onChange(kind, { locked: !settings.locked })}
          icon={settings.locked ? Lock : Unlock}
        />
      </span>
    </div>
  );
}

function TrackControlButton({
  label,
  active,
  onClick,
  icon: Icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <button
      type="button"
      data-timeline-control
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded text-white/30 transition hover:bg-white/10 hover:text-white',
        active && 'bg-brand-500/15 text-brand-300'
      )}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Ruler (ticks/labels scaled to duration through `pps`)
// ---------------------------------------------------------------------------

function Ruler({ duration, pps, height }: { duration: number; pps: number; height: number }) {
  const safeDuration = Math.max(duration, 1);
  const seconds = Math.ceil(safeDuration);
  // Pick the smallest "nice" label interval whose pixel spacing stays legible,
  // so labels never overlap regardless of zoom.
  const MIN_LABEL_PX = 56;
  const NICE = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900];
  const majorStep = NICE.find((s) => s * pps >= MIN_LABEL_PX) ?? NICE[NICE.length - 1];
  const ticks: number[] = [];
  for (let s = 0; s <= seconds; s += majorStep) ticks.push(s);
  if (ticks[ticks.length - 1] !== seconds) ticks.push(seconds);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 border-b border-white/10"
      style={{ height }}
    >
      {ticks.map((second) => (
        <div key={second} className="absolute top-0 h-full" style={{ left: second * pps }}>
          <div className="h-2 w-px bg-white/20" />
          <span className="absolute left-1 top-1.5 whitespace-nowrap text-[10px] font-semibold tabular-nums text-white/40">
            {formatDuration(second)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video clip block (thumbnail + title + duration + trim + reorder + menu)
// ---------------------------------------------------------------------------

function VideoClipBlock({
  clip,
  index,
  pps,
  selected,
  isDragging,
  isDropTarget,
  suppressDragRef,
  onSelect,
  onContextMenu,
  onTrimIn,
  onTrimOut,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  locked,
}: {
  clip: EditorTimelineClip;
  index: number;
  pps: number;
  selected: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  suppressDragRef: React.MutableRefObject<boolean>;
  onSelect: (clipId: string) => void;
  onContextMenu: (clip: EditorTimelineClip, event: ReactMouseEvent<HTMLElement>) => void;
  onTrimIn: (clipId: string, deltaSeconds: number) => void;
  onTrimOut: (clipId: string, deltaSeconds: number) => void;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  onDrop: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  locked: boolean;
}) {
  const clipDuration = safeClipDuration(clip.duration);
  const left = clip.start * pps;
  const width = Math.max(24, clipDuration * pps);
  // Thumbnail area is fixed-width but shrinks (and eventually hides) once the
  // clip block itself is too narrow to fit both a thumb and the label text.
  const thumbWidth = Math.max(0, Math.min(40, width - 20));
  const showThumb = thumbWidth >= 16;

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!locked}
      data-timeline-control
      // Flag a trim-handle grab so native drag-to-reorder stands down.
      onPointerDownCapture={(e) => {
        suppressDragRef.current = Boolean(
          (e.target as HTMLElement).closest('[data-timeline-trim]')
        );
      }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(clip.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(clip.id);
      }}
      onContextMenu={(e) => onContextMenu(clip, e)}
      className={cn(
        'group absolute inset-y-1.5 flex items-center overflow-hidden rounded-[5px] border text-left transition',
        locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing',
        selected
          ? index % 2 === 0
            ? 'border-brand-300 bg-[#5b35ff] shadow-[0_10px_28px_rgba(91,53,255,0.28)] ring-2 ring-brand-300/80'
            : 'border-indigo-300 bg-[#3f2aa8] shadow-[0_10px_28px_rgba(63,42,168,0.32)] ring-2 ring-indigo-300/80'
          : index % 2 === 0
            ? 'border-brand-500/40 bg-[#4d2bd4] hover:bg-[#5b35ff]'
            : 'border-indigo-400/40 bg-[#2d246f] hover:bg-[#3f2aa8]',
        isDragging && 'opacity-50',
        isDropTarget && 'ring-2 ring-white/70'
      )}
      style={{ left, width }}
    >
      {/* First-frame thumbnail, pinned to the block's left edge */}
      {showThumb && (
        <div
          className="relative h-full shrink-0 overflow-hidden border-r border-black/25 bg-black/30"
          style={{ width: thumbWidth }}
        >
          {clip.src ? (
            <video
              src={`${clip.src}#t=0.5`}
              className="h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Film className="h-3.5 w-3.5 text-white/50" />
            </div>
          )}
        </div>
      )}

      <div className="relative z-10 min-w-0 flex-1 px-2.5">
        <p className="truncate text-[11px] font-bold text-white">{clip.title}</p>
        <p className="mt-0.5 text-[10px] font-semibold text-white/70">
          {clip.source === 'upload' ? 'Загрузка' : 'Студия'}
        </p>
      </div>
      <span className="relative z-10 mr-1.5 shrink-0 rounded-full bg-black/35 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
        {formatDuration(clipDuration)}
      </span>

      <div data-timeline-trim>
        <TimelineTrimHandles
          pixelsPerSecond={pps}
          onTrimIn={(delta) => onTrimIn(clip.id, delta)}
          onTrimOut={(delta) => onTrimOut(clip.id, delta)}
        />
      </div>
    </div>
  );
}

/**
 * Visible transition window spanning the real overlap between clips. This is
 * intentionally a region instead of a floating dot: the user can see exactly
 * where the effect starts, ends and which boundary it belongs to.
 */
function TransitionMarker({
  windowStart,
  durationSec,
  label,
  pps,
  onSelect,
}: {
  windowStart: number;
  durationSec: number;
  label: string;
  pps: number;
  onSelect: () => void;
}) {
  const timelineWidth = durationSec * pps;
  const width = Math.max(34, timelineWidth);
  const center = (windowStart + durationSec / 2) * pps;
  return (
    <button
      type="button"
      data-timeline-control
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      title={`${label} · ${durationSec.toFixed(1)}с`}
      aria-label={`Переход ${label}, ${durationSec.toFixed(1)} секунды`}
      className="group absolute inset-y-1 z-30 -translate-x-1/2 overflow-hidden rounded-md border border-brand-300/80 bg-[linear-gradient(135deg,rgba(26,15,48,.96),rgba(91,53,255,.92),rgba(26,15,48,.96))] shadow-[0_5px_18px_rgba(0,0,0,0.55)] transition hover:border-white hover:brightness-110"
      style={{ left: center, width }}
    >
      <span className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(255,255,255,.09)_6px_8px)]" />
      <span className="pointer-events-none absolute left-1/2 top-[-20%] h-[140%] w-px -rotate-[24deg] bg-white/35" />
      <span className="relative flex h-full items-center justify-center gap-1 text-white">
        <Blend className="h-3.5 w-3.5 shrink-0 drop-shadow" />
        {width >= 58 && (
          <span className="text-[9px] font-black tabular-nums">{durationSec.toFixed(1)}с</span>
        )}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Layer / sticker lane (dark chips positioned by start/duration × pps)
// ---------------------------------------------------------------------------

function LayerLane({
  blocks,
  pps,
  emptyLabel,
  onSelect,
  onSeek,
  duration,
  snapPoints,
  snappingEnabled,
  locked,
  hidden,
}: {
  blocks: EditorOverlayLayer[];
  pps: number;
  emptyLabel: string;
  onSelect: (layerId: string) => void;
  onSeek: (time: number) => void;
  duration: number;
  snapPoints: readonly number[];
  snappingEnabled: boolean;
  locked: boolean;
  hidden: boolean;
}) {
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const updateLayer = useEditorStore((s) => s.updateLayer);

  return (
    <div className={cn('relative h-[32px] shrink-0 rounded-lg bg-white/[0.02] transition', hidden && 'opacity-30')}>
      {blocks.length === 0 ? (
        <div className="flex h-full items-center px-2.5 text-[10px] font-semibold text-white/25">
          {emptyLabel}
        </div>
      ) : (
        blocks.map((layer) => (
          <LayerBlock
            key={layer.id}
            layer={layer}
            pps={pps}
            sticker={layer.type === 'sticker'}
            duration={duration}
            selected={selectedLayerId === layer.id}
            onSelect={onSelect}
            onSeek={onSeek}
            onMove={(start, end) => updateLayer(layer.id, { start, end })}
            snapPoints={snapPoints.filter(
              (point) => Math.abs(point - layer.start) > 0.001 && Math.abs(point - layer.end) > 0.001
            )}
            snappingEnabled={snappingEnabled}
            locked={locked}
          />
        ))
      )}
    </div>
  );
}

/** Shortest a layer can be trimmed to, in seconds. */
const MIN_LAYER_DURATION = 0.2;

type LayerDragMode = 'move' | 'trim-left' | 'trim-right';

/**
 * One layer/sticker block on the timeline. Three gestures, all through the same
 * pointer flow (mirroring the clip model `time = x / pps`):
 *  - body drag → shifts the `[start, end]` window keeping its length;
 *  - left/right edge handles → trim `start` / `end` (change the frame length),
 *    clamped to `MIN_LAYER_DURATION` and `[0, duration]`;
 *  - a body press that never crosses the move threshold → click (select + seek).
 */
function LayerBlock({
  layer,
  pps,
  sticker,
  duration,
  selected,
  onSelect,
  onSeek,
  onMove,
  snapPoints,
  snappingEnabled,
  locked,
}: {
  layer: EditorOverlayLayer;
  pps: number;
  sticker?: boolean;
  duration: number;
  selected: boolean;
  onSelect: (layerId: string) => void;
  onSeek: (time: number) => void;
  onMove: (start: number, end: number) => void;
  snapPoints: readonly number[];
  snappingEnabled: boolean;
  locked: boolean;
}) {
  const dragRef = useRef<{
    mode: LayerDragMode;
    startX: number;
    origStart: number;
    origEnd: number;
    moved: boolean;
  } | null>(null);

  const left = layer.start * pps;
  const width = Math.max(20, (layer.end - layer.start) * pps);
  const maxEnd = duration > 0 ? duration : Number.POSITIVE_INFINITY;

  const beginDrag = (mode: LayerDragMode) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (locked) {
      if (mode === 'move') onSelect(layer.id);
      return;
    }
    dragRef.current = {
      mode,
      startX: e.clientX,
      origStart: layer.start,
      origEnd: layer.end,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) < 3) return; // below click threshold
    drag.moved = true;
    const dt = dx / Math.max(pps, 1);
    const len = Math.max(drag.origEnd - drag.origStart, 0);

    if (drag.mode === 'move') {
      const nextStart = Math.min(Math.max(drag.origStart + dt, 0), Math.max(0, maxEnd - len));
      const nextEnd = nextStart + len;
      const snapped = snappingEnabled
        ? snapTimelineRange(nextStart, nextEnd, snapPoints, 8 / Math.max(pps, 1))
        : { start: nextStart, end: nextEnd };
      const boundedStart = Math.min(Math.max(snapped.start, 0), Math.max(0, maxEnd - len));
      onMove(boundedStart, boundedStart + len);
    } else if (drag.mode === 'trim-left') {
      const nextStart = Math.min(Math.max(drag.origStart + dt, 0), drag.origEnd - MIN_LAYER_DURATION);
      const snapped = snappingEnabled
        ? snapTimelineTime(nextStart, snapPoints, 8 / Math.max(pps, 1)).value
        : nextStart;
      onMove(Math.min(Math.max(snapped, 0), drag.origEnd - MIN_LAYER_DURATION), drag.origEnd);
    } else {
      const nextEnd = Math.min(Math.max(drag.origEnd + dt, drag.origStart + MIN_LAYER_DURATION), maxEnd);
      const snapped = snappingEnabled
        ? snapTimelineTime(nextEnd, snapPoints, 8 / Math.max(pps, 1)).value
        : nextEnd;
      onMove(drag.origStart, Math.min(Math.max(snapped, drag.origStart + MIN_LAYER_DURATION), maxEnd));
    }
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // A body gesture that never crossed the threshold is a click → select + seek.
    if (!drag.moved && drag.mode === 'move') {
      onSelect(layer.id);
      onSeek(clampTime(layer.start, duration));
    } else {
      onSelect(layer.id);
    }
  };

  const handleClass =
    'absolute inset-y-1 z-10 w-1 cursor-ew-resize rounded-full bg-white/60 opacity-0 transition group-hover:opacity-100';

  return (
    <div
      data-timeline-control
      onPointerDown={beginDrag('move')}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={cn(
        'group absolute inset-y-1 flex items-center gap-1 overflow-hidden rounded-md border px-2 text-left text-[10px] font-semibold text-white/80 transition',
        locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing',
        sticker
          ? 'border-amber-400/30 bg-amber-400/15 hover:bg-amber-400/25'
          : 'border-white/10 bg-white/[0.06] hover:bg-white/[0.12]',
        selected && 'ring-1 ring-brand-400'
      )}
      style={{ left, width, touchAction: 'none' }}
      title={`${layer.label} · ${layer.text}`}
    >
      {layer.transitionIn && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-brand-400/80 to-transparent"
        />
      )}
      {layer.transitionOut && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-brand-400/80 to-transparent"
        />
      )}
      {cleanLayerKeyframes(layer.keyframes, layer).map((keyframe) => {
        const progress = (keyframe.time - layer.start) / Math.max(layer.end - layer.start, 0.001);
        return (
          <span
            key={keyframe.id}
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 z-[8] h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-brand-100 bg-brand-500 shadow-[0_0_0_1px_rgba(0,0,0,0.5)]"
            style={{ left: `${Math.min(100, Math.max(0, progress * 100))}%` }}
          />
        );
      })}
      {/* Left/right trim handles — change the frame's length by its edges. */}
      <div
        onPointerDown={beginDrag('trim-left')}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(handleClass, 'left-0.5', selected && 'opacity-100')}
        aria-label="Обрезать начало"
      />
      <span className="pointer-events-none truncate">{layer.label}</span>
      <div
        onPointerDown={beginDrag('trim-right')}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(handleClass, 'right-0.5', selected && 'opacity-100')}
        aria-label="Обрезать конец"
      />
    </div>
  );
}
